import { useTheme } from "@mui/material";
import type { ComponentProps } from "react";

import { AskMaraDisabledSvg, AskMaraSvg } from "../assets/svg";
import { IconButton } from "../icon-button/IconButton";

const BOX_SHADOW_LAYER =
  /(-?[\d.]+)px\s+(-?[\d.]+)px\s+([\d.]+)px(?:\s+-?[\d.]+px)?\s+(rgba?\([^)]*\))/g;

// The theme blur radii are sized for large surfaces; on a 24px icon they smear
// into a round halo with visible banding rings, so the blur is tightened to keep a smooth hexagon-shaped gradient.
const SHADOW_BLUR_SCALE = 0.2;

function parseBoxShadow(boxShadow: string) {
  return Array.from(boxShadow.matchAll(BOX_SHADOW_LAYER), (match) => ({
    dx: Number(match[1]),
    dy: Number(match[2]),
    blur: Number(match[3]),
    color: match[4],
  }));
}

export interface AskMaraIconProps
  extends Omit<ComponentProps<typeof IconButton>, "children" | "tooltip"> {
  /**
   * Tooltip and accessible label of the button.
   * @default "Ver insight"
   */
  tooltip?: string;
}

export function AskMaraIcon({
  tooltip = "Ver insight",
  disabled,
  ...props
}: AskMaraIconProps) {
  const theme = useTheme();
  const layers = parseBoxShadow(theme.shadows[4]);
  const Icon = disabled ? AskMaraDisabledSvg : AskMaraSvg;

  return (
    <IconButton
      tooltip={tooltip}
      aria-label={tooltip}
      tooltipPlacement="bottom-end"
      variant="text"
      disabled={disabled}
      {...props}
    >
      {/* Icon-sized positioned wrapper: keeps shadow copies aligned with the foreground icon regardless of button size/padding overrides */}
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          width: 24,
          height: 24,
        }}
      >
        {/* Each theme shadow layer gets its own icon copy: chaining several drop-shadows on one element compounds them into wave-like rings */}
        {layers.map(({ dx, dy, blur, color }) => (
          <Icon
            key={`${dx}-${dy}-${blur}-${color}`}
            aria-hidden="true"
            width={24}
            height={24}
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              filter: `drop-shadow(${dx}px ${dy}px ${blur * SHADOW_BLUR_SCALE}px ${color})`,
            }}
          />
        ))}
        <Icon width={24} height={24} style={{ position: "relative" }} />
      </span>
    </IconButton>
  );
}
